import { toAbsoluteUrl } from "../../_theme/helpers";
import { envConfig } from "../../utils/config";
import HeroSection from "./Components/HeroSection";
import { Link } from "react-router-dom";
import VideoWithFallback from "./Components/VideoWithFallback";

// Type definitions
interface Card {
    id: number;
    title: string;
    department: string;
    description: string;
    imageUrl: string;
    url: string
}

interface Feature {
    id: number;
    text: string;
}

interface Importance {
    id: number;
    text: string;
}

// Constants
const CARDS_DATA: Card[] = [
    {
        id: 1,
        title: "Western Cape Infrastructure Framework 2050",
        department: "Department of Infrastructure",
        description: "The WCIF 2050 sets out the scientific basis of the framework and demonstrates how the framework integrates science with policy, governance, compliance and efficiency aspects into a whole.",
        imageUrl: "media/images/about-box-img-1.jpg",
        url: "/pages/western-cape-infrastructure-framework"
    },
    {
        id: 2,
        title: "Western Cape Infrastructure Strategy 2050",
        department: "Department of Infrastructure",
        description: "The WCIS 2050 adopts the Provincial strategic priorities and guides prioritised infrastructure sector strategies across the short-medium-long term. It also enables the WCGs IMC, ITC and RFP structures.",
        imageUrl: "media/images/about-box-img-2.jpg",
        url: "/pages/western-cape-infrastructure-strategy"
    },
    {
        id: 3,
        title: "Western Cape Infrastructure Implementation Plan 2050",
        department: "Department of Infrastructure",
        description: "The WCIIP 2050 embeds the strategies as articulated in the WCIS 2050 and translates them into detailed operational actions and activities. It serves to tie the framing, and its core strategies into direct practices focused to attain the 2050 vision.",
        imageUrl: "media/images/about-box-img-3.jpg",
        url: "/pages/western-cape-infrastructure-implementation-plan"
    }
];

const FEATURES_DATA: Feature[] = [
    {
        id: 1,
        text: "Consistent use and compliance to core infrastructure prescripts."
    },
    {
        id: 2,
        text: "Harmonisation of the NT FIDPM and ISO 9001 Infrastructure methodologies have been gamified for ease of use and compliance, enabling ease of doing business and ease of governance in an ecosystem."
    },
    {
        id: 3,
        text: "Monitoring and evaluation insights to build investor ready pipeline."
    },
    {
        id: 4,
        text: "A federated and ubiquitous architecture that allows for data sovereignty to individual departments, and organisations."
    }
];

const IMPORTANCE_DATA: Importance[] = [
    {
        id: 1,
        text: "It counters infrastructure legacy spatial dimensions"
    },
    {
        id: 2,
        text: "Promotes and facilities citizen centric infrastructure vision."
    },
    {
        id: 3,
        text: "Offers the connection between science and practice through sharing knowledge."
    }
];

const IntroductionSection: React.FC = () => (
    <div className="row flex-column-reverse flex-lg-row g-10 section-padding bg-white" id="introduction-section">
        <div className="col-md-9 left-content px-10 px-lg-20 px-md-10 px-sm-15">
            <h2 className="section-title mb-2">Welcome to the Western Cape Infrastructure Platform</h2>
            <p className="section-sub-title mb-4">Where Vision Becomes Reality</p>

            <p className="section-content">
                The Western Cape Infrastructure Platform is an intelligent digital space designed to drive the Western Cape’s Infrastructure investments. In this space WC public and private sector partners connect seamlessly to drive infrastructure transformation. This platform supports the scientific principles embedded in WCIF 2050 (Framework) and further enables detailed strategies as contained in WCIS 2050 (Strategy), supporting the implementation of WCIIP 2050 (Implementation Plan)
            </p>

            <p className="section-content">
                At its core are three foundational elements: the WCIF 2050 (Framework), WCIS 2050 (Strategy), and WCIIP 2050 (Implementation Plan). These and other infrastructure prescripts form the foundational specification in the infrastructure platform.</p>

            <p className="section-content">
                The platform promotes governance using clear policy prescript directionality. This is further integrated to deliver the platform’s promise of consistent integration and interoperability. The platform is designed for federation and ubiquity to ensure smarter and faster delivery across the infrastructure lifecycle and its stage gates. It also uses AI to facilitate and embed “ease-of-business” and “ease-of-government” into everyday practices to make the infrastructure Value Chain experience smoother, more intuitive, and impactful.            </p>
        </div>
        <div className="col-md-3 px-10 px-sm-15 px-md-10 px-lg-0">
            <img
                src={toAbsoluteUrl("media/images/about_intro_img.jpg")}
                alt="about-us-intro-image"
                className="w-100 h-100"
            />
        </div>
    </div>
);

const FlipCard: React.FC<{ card: Card }> = ({ card }) => (
    <div className="flip-card d-flex align-items-center justify-content-center">
        <div className="flip-card-inner h-75 w-100 w-sm-50 w-md-100">
            <div className="flip-card-front h-100 w-100">
                <img
                    src={toAbsoluteUrl(card.imageUrl)}
                    alt={card.title}
                // className="object-fit-cover"
                />
                <div className="details h-50">
                    <p className="box-tag">{card.department}</p>
                    <p className="box-title">{card.title}</p>
                </div>
            </div>
            <div className="flip-card-back h-100 w-100">
                <div className="inner-flip-card-back h-100 w-100 d-flex align-items-center justify-content-center flex-column overflow-hidden text-center">
                    <p className="text-light">{card.description}</p>
                    <Link to={card.url} className="mb-5 fw-bold text-white">Read More</Link>
                </div>
            </div>
        </div>
    </div>
);

const CardsSection: React.FC = () => (
    <div className="department py-20 aboutUs-section d-flex align-items-center">
        <div className=" public-pages-container aboutUs-row row g-4 w-100 align-items-stretch">
            {CARDS_DATA.map((card) => (
                <div className="boxes-row col-lg-4 d-flex justify-content-center m-0" key={card.id}>
                    <div className="box w-75">
                        <FlipCard card={card} />
                    </div>
                </div>
            ))}
        </div>
        {/* </div> */}
    </div>
);

const FeaturesSection: React.FC = () => {
    const renderFeatureItems = (features: Feature[]) => (
        <ul className="fw-light">
            {features.map((feature) => (
                <li key={feature.id} className="section-content mb-3">
                    {feature.text}
                </li>
            ))}
        </ul>
    );

    const renderImportanceItems = (importance: Importance[]) => (
        <ul className="fw-light">
            {importance.map((item) => (
                <li key={item.id} className="section-content mb-3">
                    {item.text}
                </li>
            ))}
        </ul>
    );

    return (
        <div className="row flex-column flex-lg-row g-10 section-padding bg-white">
            <div className="col-md-3 px-10 px-lg-0 px-md-10 px-sm-15">
                <VideoWithFallback
                    videoSrc={`${envConfig.APP_MEDIA_URL}videos/public-pages/mean-for-you.mp4`}
                    fallbackSrc={`${envConfig.APP_MEDIA_URL}images/public-pages/mean-for-you.webp`}
                    alt="Mean For You"
                />
            </div>

            <div className="col-md-9 right-content-section px-10 px-sm-15 px-md-10 px-lg-20">
                <div className="mb-15">
                    <h2 className="section-title mb-2">So What Does This Mean?</h2>
                    <p className="mb-4 section-sub-title">
                        The platform is a tool that enables:
                    </p>
                    {renderFeatureItems(FEATURES_DATA)}
                </div>

                <div>
                    <h2 className="section-title mb-2">Why is this important?</h2>
                    {renderImportanceItems(IMPORTANCE_DATA)}
                </div>
            </div>
        </div>
    );
};
// Main Component
const AboutUs: React.FC = () => {
    return (
        <div className="overflow-y-auto overflow-x-hidden about-us ">
            <HeroSection
                videoSrc={`${envConfig.APP_MEDIA_URL}videos/public-pages/about-video.mp4`}
                title="About the platform"
                subtitle="An infrastructure platform for a resilient, inclusive and Futures-oriented Western Cape."
                fallbackImage={`${envConfig.APP_MEDIA_URL}images/public-pages/about-video.webp`}
                scrollParentId="introduction-section"
            />
            <IntroductionSection />
            <CardsSection />
            <FeaturesSection />
        </div>
    );
};

export default AboutUs;